/*
 * Formatting money in the currency the visitor is actually looking at.
 *
 * The membership pages rewrite prices in JavaScript when a sale is running,
 * and they used to do it with a hardcoded dollar sign. The store sells into
 * 237 countries through the currency picker, so for anyone outside the US that
 * printed the right number with the wrong symbol — plausible enough to be
 * believed, which is worse than being obviously broken.
 *
 * Rather than guess at a format, this learns one from a price Liquid has
 * already rendered on the same page. Given the raw cents behind that price and
 * the string Shopify produced from them, it works out the prefix, the suffix,
 * the separators and how many decimal places the currency uses — so a rewritten
 * price is formatted exactly like the one struck through beside it. Intl would
 * be close, but "€7.99" next to Shopify's "€7,99" reads as a bug.
 */
(function () {
  'use strict';

  var NUMERIC = /\d[\d.,   ]*\d|\d/;

  /**
   * @param cents    the raw integer behind the rendered price
   * @param rendered what Shopify's money filter made of it
   * @returns a function taking cents and returning a string in the same style,
   *          or null when the pair cannot be read.
   */
  function learn(cents, rendered) {
    var amount = Number(cents);
    var sample = String(rendered == null ? '' : rendered).trim();
    if (!Number.isFinite(amount) || !sample) return null;

    var match = sample.match(NUMERIC);
    if (!match) return null;

    var numeric = match[0];
    var prefix = sample.slice(0, match.index);
    var suffix = sample.slice(match.index + numeric.length);

    /* The decimal separator is whichever of . or , is last and has exactly two
       digits after it. Anything else — ¥799, or 1.234 meaning one thousand —
       means this currency is shown without them. */
    var decimals = 0;
    var decimalSeparator = '';
    var tail = numeric.match(/[.,](\d{2})$/);
    if (tail) {
      decimals = 2;
      decimalSeparator = tail[0].charAt(0);
    }

    var groupSeparator = decimalSeparator === ',' ? '.' : ',';

    /* Confirm the reading round-trips. If it does not, the sample was not what
       it looked like and guessing further would be worse than declining. */
    var digitsOnly = numeric.replace(/[^\d]/g, '');
    var expected = decimals === 2 ? Math.round(amount) : Math.round(amount / 100);
    var read = Number(digitsOnly);
    if (decimals === 2 ? read !== expected : read !== expected) return null;

    return function format(nextCents) {
      var value = Number(nextCents);
      if (!Number.isFinite(value)) return sample;

      var negative = value < 0;
      value = Math.abs(value);

      var whole = decimals === 2 ? Math.floor(value / 100) : Math.round(value / 100);
      var fraction = decimals === 2 ? String(Math.round(value % 100)).padStart(2, '0') : '';

      var grouped = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, groupSeparator);
      var body = decimals === 2 ? grouped + decimalSeparator + fraction : grouped;

      return (negative ? '-' : '') + prefix + body + suffix;
    };
  }

  /**
   * Learns from the first element on the page carrying both a rendered price
   * and the cents behind it, so a caller does not have to know where to look.
   */
  function learnFrom(root, selector) {
    var scope = root && root.querySelector ? root : document;
    var nodes = scope.querySelectorAll(selector || '[data-od-money-cents]');

    for (var i = 0; i < nodes.length; i++) {
      var format = learn(nodes[i].getAttribute('data-od-money-cents'), nodes[i].textContent);
      if (format) return format;
    }

    return null;
  }

  window.ODMoney = { learn: learn, learnFrom: learnFrom };
})();
